import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../../config';
import { useOrganization } from '../../context/OrganizationContext';
import { fetchWithOrg } from '../../utils/api';
import { useToast } from '../../hooks/use-toast';

import {
  ArrowsClockwise,
  Calendar,
  CaretRight,
  CheckCircle,
  Clock,
  Pause,
  PencilSimple,
  Play,
  Plus,
  Trash,
  WarningCircle,
  XCircle
} from '@phosphor-icons/react';
/**
 * Schedules Page - V1 Agent Scheduler
 * Allows admins to view, manage, and create scheduled agent runs.
 */
const Schedules = () => {
  const navigate = useNavigate();
  const { activeOrganization } = useOrganization();
  const { toast } = useToast();
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (activeOrganization) {
      fetchSchedules();
    }
  }, [activeOrganization]);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/schedules`,
        {},
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        const data = await response.json();
        setSchedules(data.schedules || []);
      }
    } catch (err) {
      console.error('Error fetching schedules:', err);
      toast({
        title: 'Error',
        description: 'Failed to load schedules',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (scheduleId) => {
    try {
      setActionLoading(scheduleId);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/schedules/${scheduleId}/pause`,
        { method: 'POST' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        toast({ title: 'Schedule paused' });
        fetchSchedules();
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to pause schedule', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async (scheduleId) => {
    try {
      setActionLoading(scheduleId);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/schedules/${scheduleId}/resume`,
        { method: 'POST' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        toast({ title: 'Schedule resumed' });
        fetchSchedules();
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to resume schedule', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    
    try {
      setActionLoading(scheduleId);
      const response = await fetchWithOrg(
        `${BACKEND_URL}/api/schedules/${scheduleId}`,
        { method: 'DELETE' },
        activeOrganization.organization_id
      );
      
      if (response.ok) {
        toast({ title: 'Schedule deleted' });
        fetchSchedules();
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete schedule', variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const formatScheduleType = (type, config) => {
    const hour = config?.hour ?? 9;
    const minute = config?.minute ?? 0;
    const dayOfWeek = config?.day_of_week ?? 0;
    const dayOfMonth = config?.day_of_month ?? 1;
    
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    
    switch (type) {
      case 'hourly':
        return `Every hour at :${minute.toString().padStart(2, '0')}`;
      case 'daily':
        return `Daily at ${time}`;
      case 'weekly':
        return `Every ${days[dayOfWeek]} at ${time}`;
      case 'monthly':
        return `Monthly on day ${dayOfMonth} at ${time}`;
      default:
        return 'Manual';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status, lastRunStatus) => {
    if (status === 'paused') {
      return (
        <span className="px-2 py-1 text-xs rounded bg-gray-500/20 text-gray-400 border border-gray-500/30">
          Paused
        </span>
      );
    }
    
    if (lastRunStatus === 'completed') {
      return (
        <span className="px-2 py-1 text-xs rounded bg-green-500/20 text-green-400 border border-green-500/30 flex items-center gap-1">
          <CheckCircle size={12} /> Active
        </span>
      );
    }
    
    if (lastRunStatus === 'error') {
      return (
        <span className="px-2 py-1 text-xs rounded bg-red-500/20 text-red-400 border border-red-500/30 flex items-center gap-1">
          <XCircle size={12} /> Error
        </span>
      );
    }
    
    return (
      <span className="px-2 py-1 text-xs rounded bg-blue-500/20 text-blue-400 border border-blue-500/30">
        Active
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] via-[#1a1a2e] to-[#0A0A0F] p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-gray-700 rounded"></div>
            <div className="h-64 bg-gray-800 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] via-[#1a1a2e] to-[#0A0A0F] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Clock className="text-[#6366F1]" size={24} />
              Scheduled Runs
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Automate agent runs on a cadence
            </p>
          </div>
          
          <button
            onClick={fetchSchedules}
            className="px-3 py-2 glass text-gray-400 hover:text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <ArrowsClockwise size={16} />
            ArrowsClockwise
          </button>
        </div>

        {/* Empty State */}
        {schedules.length === 0 && (
          <div className="glass rounded-2xl p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#6366F1]/20 flex items-center justify-center">
              <Calendar size={32} className="text-[#6366F1]" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">No Scheduled Runs</h2>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Schedule agents to run automatically on a cadence. Open an agent and click &quot;Schedule&quot; to get started.
            </p>
            <button
              onClick={() => navigate('/partner-pro/workspace/my-agents')}
              className="px-4 py-2 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2 mx-auto"
            >
              <Plus size={16} />
              Go to My Agents
            </button>
          </div>
        )}

        {/* Schedules List */}
        {schedules.length > 0 && (
          <div className="glass rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[rgba(255,255,255,0.1)]">
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Agent</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Schedule</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Next Run</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Last Run</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Status</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((schedule) => (
                  <tr 
                    key={schedule.id} 
                    className="border-b border-[rgba(255,255,255,0.05)] hover:bg-[rgba(255,255,255,0.02)] transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="font-medium text-white">{schedule.agent_name || 'Unknown Agent'}</div>
                      <div className="text-xs text-gray-500">{schedule.timezone}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">
                        {formatScheduleType(schedule.schedule_type, schedule.schedule_config)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">{formatDate(schedule.next_run_at)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">{formatDate(schedule.last_run_at)}</div>
                      {schedule.last_run_session_id && (
                        <button
                          onClick={() => navigate(`/partner-pro/workspace/sessions/${schedule.last_run_session_id}`)}
                          className="text-xs text-[#6366F1] hover:underline flex items-center gap-1 mt-1"
                        >
                          View result <CaretRight size={12} />
                        </button>
                      )}
                      {schedule.last_error && (
                        <div className="text-xs text-red-400 mt-1 truncate max-w-[200px]" title={schedule.last_error}>
                          {schedule.last_error}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(schedule.status, schedule.last_run_status)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {schedule.status === 'active' ? (
                          <button
                            onClick={() => handlePause(schedule.id)}
                            disabled={actionLoading === schedule.id}
                            className="p-2 glass text-gray-400 hover:text-amber-400 rounded-lg transition-colors disabled:opacity-50"
                            title="Pause"
                          >
                            <Pause size={16} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleResume(schedule.id)}
                            disabled={actionLoading === schedule.id}
                            className="p-2 glass text-gray-400 hover:text-green-400 rounded-lg transition-colors disabled:opacity-50"
                            title="Resume"
                          >
                            <Play size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(schedule.id)}
                          disabled={actionLoading === schedule.id}
                          className="p-2 glass text-gray-400 hover:text-red-400 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Info Note */}
        <div className="mt-6 p-4 bg-[rgba(99,102,241,0.1)] border border-[rgba(99,102,241,0.2)] rounded-lg">
          <div className="flex items-start gap-3">
            <WarningCircle size={18} className="text-[#6366F1] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-gray-300">
                <strong>V1 Scheduler</strong>: Only text-only and automation-ready agents can be scheduled.
                Integration-dependent agents will be supported in a future update.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Schedules;
